import { useState } from 'react';
import tzLookup from 'tz-lookup';
import { DateTime } from 'luxon';

const DAYSOFWEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Modified function: gets current local date/time for given lat/lng (default sydney)
const getLocalCurrentDateTime = (latitude: number = -33.86, longitude: number = 151.21) => {
  const timezone = tzLookup(latitude, longitude);
  const now = DateTime.now().setZone(timezone);

  const pad = (n: number) => String(n).padStart(2, '0');

  const year = now.year;
  const month = pad(now.month);
  const day = pad(now.day);
  const hours = pad(now.hour);
  const dayOfWeek = now.weekday - 1; // Luxon: 1=Monday, 7=Sunday

  return {
    dateTime: `${year}-${month}-${day}T${hours}:00:00`,
    day: DAYSOFWEEK[dayOfWeek],
    timezone,
  };
};

const getDayOfWeek = (dateString) => {
  return DAYSOFWEEK[DateTime.fromISO(dateString).weekday - 1];
};

const useDateHandler = () => {
  const [convertedDateTimes, setConvertedDateTimes] = useState<{
    [dateTime: string]: number;
  }>({});
  const [convertedCurrentDateTime, setConvertedCurrentDateTime] = useState<string>('');
  const [selectedDateTimes, setSelectedDateTimes] = useState<[string, number][]>([]);

  const setUpDate = (dateTime: string): string => {
    const day = getDayOfWeek(dateTime.split('T')[0]);
    return `${day} ${dateTime.split('T')[1].replace('Z', '')}`;
  };

  const setUpAllDates = (capacities) => {
    if (!capacities || typeof capacities !== 'object') {
      console.warn('setUpAllDates: capacities is null, undefined, or not an object:', capacities);
      setConvertedDateTimes({});
      return;
    }

    try {
      const result: { [dateTime: string]: number } = {};
      Object.keys(capacities).forEach((dateStr) => {
        return (result[setUpDate(dateStr)] = capacities[dateStr]);
      });
      setConvertedDateTimes(result);
      return result;
    } catch (error) {
      console.error('Error in setUpAllDates:', error, 'capacities:', capacities);
      setConvertedDateTimes({});
    }
  };

  const getDayCapacities = (dateTime: string, customCapacities = null): [string, number][] => {
    const day = dateTime.split(' ')[0];
    return Object.entries(customCapacities || convertedDateTimes)
      .filter(([datetime, _]) => {
        return datetime.includes(day);
      })
      .map(([datetime, capacity]) => [datetime, Number(capacity)]) as [string, number][];
  };

  // Get current capacity based on location's local time
  const getCurrentCapacity = () => {
    return convertedDateTimes[convertedCurrentDateTime];
  };

  return {
    convertedCurrentDateTime,
    setConvertedCurrentDateTime,
    selectedDateTimes,
    setSelectedDateTimes,
    setUpDate,
    setUpAllDates,
    getDayCapacities,
    getCurrentCapacity,
  };
};

export { DAYSOFWEEK, getLocalCurrentDateTime, getDayOfWeek, useDateHandler };
